import React, { useCallback, useContext, useRef } from 'react';
import { FocusLayerSize } from '../../feature-service-types';
import { LayerContentHTML } from '@oneaudi/fa-one-layer';
import { HeaderStyledNavItem, HeaderStyledNavItemLink } from './AudiHeaderNavItemStyles';
import {
  createResetActiveItemAction,
  createSetActiveItemAction,
} from '../../actions/header-actions';
import { getClassNamesForLinks, getLinkTarget } from '../../services';

import type { AudiHeaderNavItemProps } from '../../interfaces/header-components.interfaces';
import AudiHeaderSubNav from '../AudiHeaderSubNav/AudiHeaderSubNav';
import { useStateSwitchTimeOutHook } from '../../services/hooks';
import { mainNavigationLinkClick, mainNavigationToggle } from '../../utils/tracking';
import type { Link } from '../../interfaces/header-response.interfaces';
import { scriptRegex } from '../../utils/string-utils';
import { ServiceContext } from '../../context/ServiceContext';
import useKeyboard from '../../hooks/use-keyboars-events';
import { keyTypes } from '../../utils/a11y';
import { NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME } from '../../utils/globals';
import { getCategoryDetails } from '../../utils/get-category-details';

const AudiHeaderNavItem: React.FC<AudiHeaderNavItemProps> = ({
  headerDispatch,
  headerNavigationItemsAmount,
  headerState,
  isSubNavOpen,
  index,
  item,
  onNavigationLinkClick,
  useFootnoteReferenceServiceTextParserHook,
  useOneLayer,
}) => {
  const { headerStateService, layerManager, marketContextService, trackingService } =
    useContext(ServiceContext);
  const { stateSwitch: addClosingAnimation } = useStateSwitchTimeOutHook(440);
  const navigationElement = useRef<HTMLDivElement>(null);
  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });
  const dangerousHTML = useFootnoteReferenceServiceTextParserHook(
    item.Link?.Text?.replace(scriptRegex, ' illegal script '),
  );

  const [defaultHandlerAttached, setDefaultHandlerAttached] = React.useState<boolean>(false);

  const isSamePageAnchorLink = useCallback((url?: string) => {
    if (!url || typeof window === 'undefined') {
      return false;
    }

    try {
      const currentUrl = new URL(window.location.href);
      const targetUrl = new URL(url, currentUrl.href);

      return (
        !!targetUrl.hash &&
        targetUrl.origin === currentUrl.origin &&
        targetUrl.pathname === currentUrl.pathname &&
        targetUrl.search === currentUrl.search
      );
    } catch {
      return false;
    }
  }, []);

  const defaultPreventAnchorClickHandler = React.useCallback(
    (e: { preventDefault: () => void }) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (item.SubNavigation?.length > 0) {
        e.preventDefault();
      }
    },
    [item],
  );

  const toggleSubNavigation: (event: any, forceClose?: boolean) => void = (event, forceClose) => {
    if (event && event.preventDefault) event.preventDefault();

    trackingService?.track(
      mainNavigationToggle(!isSubNavOpen ? 'open' : 'close', event.currentTarget, index),
    );
    onMenuItemClick(event, item.Link);
    if (
      index !== headerState.activeItem.index &&
      !forceClose &&
      typeof forceClose === 'undefined'
    ) {
      headerDispatch(
        createSetActiveItemAction({
          anchor: event.currentTarget,
          headerStateService,
          index,
          showSearch: false,
          showDealer: false,
          showDealerSearch: false,
          showLoginFlyout: false,
          showWistListFlyout: false,
          openDealerDetails: false,
        }),
      );
    } else {
      headerDispatch(createResetActiveItemAction(headerStateService));
      addClosingAnimation();
    }
  };

  const onMenuItemClick = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      listItem?: Link,
      shouldCloseMobileMenu?: boolean,
    ): void => {
      if (
        listItem?.Target === '_layer' &&
        listItem.Type === 'default' &&
        layerManager &&
        useOneLayer
      ) {
        event.preventDefault();
        event.stopPropagation();

        layerManager.openFocusLayer(
          () => (
            <LayerContentHTML
              audiMarketContextService={marketContextService}
              fefaStyleConfig={{
                omitBottomSpacing: 'true',
                omitSideSpacing: 'true',
                uiTheme: 'light',
              }}
              url={listItem.Url}
            />
          ),
          {},
          {
            size: FocusLayerSize.A,
            userCloseable: true,
          },
        );
      }

      // make sure overlays are closed
      closeSubNavigation(event, true);

      if (shouldCloseMobileMenu && isSamePageAnchorLink(listItem?.Url)) {
        onNavigationLinkClick?.();
      }
    },
    [isSamePageAnchorLink, marketContextService, layerManager, onNavigationLinkClick, useOneLayer],
  );
  // this implementation is about adding a new event handler which
  // is called before the original react handler. When integrating
  // GTM the GTM is executed before the React-event for toggleing the submenu.
  // This leads to the effect that otherwise the default
  // behavior of the link is reactivated.
  const anchorElement = React.useRef(null);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore

  React.useEffect(() => {
    if (typeof window !== 'undefined' && anchorElement && anchorElement.current) {
      const localAnchorElement: HTMLElement = anchorElement.current;
      if (!defaultHandlerAttached) {
        localAnchorElement.addEventListener('click', defaultPreventAnchorClickHandler);
        setDefaultHandlerAttached(true);

        return (): void => {
          localAnchorElement.removeEventListener('click', defaultPreventAnchorClickHandler);
        };
      }
    }
  }, [item, defaultHandlerAttached, defaultPreventAnchorClickHandler]);

  const onMainMenuClick = useCallback(
    (event: any) => {
      if (item?.Link) {
        onMenuItemClick(event, item.Link, true);
        trackingService?.track(mainNavigationLinkClick('click', event.currentTarget, index));
      }
    },

    [item.Link, onMenuItemClick],
  );

  const closeSubNavigation: (event: any, isPreventDefault?: boolean) => void = (
    event,
    isPreventDefault,
  ) => {
    if (!isPreventDefault) event.preventDefault();
    headerDispatch(createResetActiveItemAction(headerStateService));
  };

  useKeyboard(
    'keydown',
    (event) => {
      if (event.key === keyTypes.ArrowDown) {
        if (anchorElement?.current) toggleSubNavigation(anchorElement.current);
      }
    },
    anchorElement,
  );

  const { isBasicCategory, validBlocks } = getCategoryDetails(item);

  const hasSubNav = isBasicCategory ? item?.SubNavigation?.length : validBlocks.length;

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {hasSubNav ? (
        <HeaderStyledNavItem
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          isSmallView={isSmallView}
          ref={navigationElement}
          className="top-nav-items"
        >
          <HeaderStyledNavItemLink
            className={`${getClassNamesForLinks(item.Link, useOneLayer)} ${NAVIGATION_CLASSNAME} ${NAVIGATION_MOBILE_CLASSNAME} ${headerState.activeItem.index === index ? 'top-active' : ''}`}
            data-testid="one-header-nav-item-link"
            data-audi-core-tracking-exclude="true"
            headerNavigationItemsAmount={headerNavigationItemsAmount}
            isSmallView={isSmallView}
            href={item?.Link?.Url}
            onClick={toggleSubNavigation}
            ref={anchorElement}
            aria-label={item?.Link?.AriaLabel}
            target={getLinkTarget(item?.Link)}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: item?.Link ? dangerousHTML : '',
              }}
            />
            <svg height="24" width="24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 16.9l5.5-5.4L10 6.1" fill="none" stroke="currentColor" />
            </svg>
          </HeaderStyledNavItemLink>
          <AudiHeaderSubNav
            mainCategoryPosition={index}
            mainCategoryLabel={item?.Link ? dangerousHTML : ''}
            closeSubNavigation={(e) => {
              closeSubNavigation(e);
            }}
            headerNavigationItemsAmount={headerNavigationItemsAmount}
            isSmallView={isSmallView}
            isSubNavOpen={headerState.activeItem.index === index}
            onItemClick={(event, listItem) => {
              onMenuItemClick(event, listItem, true);
            }}
            subNavItem={item}
            useFootnoteReferenceServiceTextParserHook={useFootnoteReferenceServiceTextParserHook}
            useOneLayer={useOneLayer}
            mainNavIndex={index}
          />
        </HeaderStyledNavItem>
      ) : (
        <HeaderStyledNavItem
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          isSmallView={isSmallView}
          className="top-nav-items"
        >
          {item?.Link ? (
            <HeaderStyledNavItemLink
              className={`${getClassNamesForLinks(item.Link, useOneLayer)} ${NAVIGATION_CLASSNAME} ${NAVIGATION_MOBILE_CLASSNAME} ${headerState.activeItem.index === index ? 'top-active' : ''}`}
              data-testid="one-header-nav-item-link"
              data-audi-core-tracking-exclude="true"
              headerNavigationItemsAmount={headerNavigationItemsAmount}
              isSmallView={isSmallView}
              href={item?.Link?.Url}
              onClick={onMainMenuClick}
              aria-label={item?.Link?.AriaLabel}
              target={item?.Link?.Target}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: item?.Link ? dangerousHTML : '',
                }}
              />
            </HeaderStyledNavItemLink>
          ) : null}
        </HeaderStyledNavItem>
      )}
    </>
  );
};

export default AudiHeaderNavItem;
